import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { invoicesApi } from '../../api/endpoints';
import { InvoiceDetail, InvoiceTypeMap, InvoiceStatusMap } from '../../types';
import {
  FiSend,
  FiDownload,
  FiTrash2,
  FiPlus,
  FiX,
  FiEye,
  FiArrowLeft,
  FiFileText,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const InvoiceDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [creditNoteData, setCreditNoteData] = useState({
    creditNoteNumber: '',
    creditNoteDate: new Date().toISOString().split('T')[0],
    reason: '',
    items: [] as { amazonProductId: string; description: string; quantity: number; unitPrice: number; taxRate: number; hsCode: string }[],
  });
  const [creatingCn, setCreatingCn] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await invoicesApi.get(id);
      setInvoice(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('invoices.detail.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleSubmitToAmazon = useCallback(async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await invoicesApi.submit(id);
      toast.success(t('invoices.detail.submitSuccess'));
      fetchInvoice();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('invoices.detail.submitError'));
    } finally {
      setSubmitting(false);
    }
  }, [id, fetchInvoice, t]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await invoicesApi.delete(id);
      toast.success(t('invoices.detail.deleteSuccess'));
      navigate('/invoices');
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('invoices.detail.deleteError'));
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [id, navigate, t]);

  const handleDownloadPdf = useCallback(async () => {
    if (!id) return;
    try {
      const response = await invoicesApi.getPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faktura-${invoice?.invoiceNumber || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('invoices.detail.downloadPdfError'));
    }
  }, [id, invoice, t]);

  const handlePreviewPdf = useCallback(async () => {
    if (!id) return;
    try {
      const response = await invoicesApi.getPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setShowPdfPreview(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('invoices.detail.previewPdfError'));
    }
  }, [id, t]);

  const [generatingPdf, setGeneratingPdf] = useState(false);

  const handleGeneratePdf = useCallback(async () => {
    if (!id) return;
    setGeneratingPdf(true);
    try {
      const response = await invoicesApi.generatePdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setShowPdfPreview(true);
      toast.success(t('invoices.detail.pdfGenerated'));
      fetchInvoice(); // refresh hasPdf
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('invoices.detail.generatePdfError'));
    } finally {
      setGeneratingPdf(false);
    }
  }, [id, fetchInvoice, t]);

  const closePdfPreview = useCallback(() => {
    setShowPdfPreview(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [pdfUrl]);

  // Credit note
  const openCreditNoteModal = useCallback(() => {
    if (!invoice) return;
    setCreditNoteData({
      creditNoteNumber: `CN/${invoice.invoiceNumber}`,
      creditNoteDate: new Date().toISOString().split('T')[0],
      reason: '',
      items: invoice.items.map((i) => ({
        amazonProductId: i.amazonProductId,
        description: i.description,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
        taxRate: i.taxRate,
        hsCode: i.hsCode || '',
      })),
    });
    setShowCreditNoteModal(true);
  }, [invoice]);

  const handleCreateCreditNote = useCallback(async () => {
    if (!id) return;
    setCreatingCn(true);
    try {
      const response = await invoicesApi.createCreditNote(id, creditNoteData);
      toast.success(t('invoices.detail.creditNoteCreated'));
      setShowCreditNoteModal(false);
      navigate(`/invoices/${response.data.id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('invoices.detail.creditNoteError'));
    } finally {
      setCreatingCn(false);
    }
  }, [id, creditNoteData, navigate, t]);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('pl-PL', { style: 'currency', currency: invoice?.currency || 'EUR' });

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="page-container page-container--wide">
        <div className="alert alert-danger">{t('invoices.detail.notFound')}</div>
        <Link to="/invoices" className="btn btn-secondary">
          {t('invoices.detail.backToList')}
        </Link>
      </div>
    );
  }

  const isDraft = invoice.status === 0;
  const typeName = InvoiceTypeMap[invoice.invoiceType] || t('invoices.typeInvoice');
  const statusName = InvoiceStatusMap[invoice.status] || t('invoices.detail.unknown');

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/invoices" className="btn btn-link">
            <FiArrowLeft /> {t('invoices.title')}
          </Link>
          <h1>
            {typeName} {invoice.invoiceNumber}
          </h1>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {isDraft && (
            <>
              <button
                className="btn btn-primary"
                onClick={() => setShowSubmitConfirm(true)}
                disabled={submitting}
              >
                <FiSend /> {submitting ? t('invoices.sending') : t('invoices.submitToAmazon')}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
              >
                <FiTrash2 /> {deleting ? t('invoices.detail.deleting') : t('common.delete')}
              </button>
            </>
          )}
          {invoice.invoiceType === 0 && invoice.status !== 0 && (
            <button className="btn btn-secondary" onClick={openCreditNoteModal}>
              <FiPlus /> {t('invoices.typeCreditNote')}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={handleGeneratePdf}
            disabled={generatingPdf}
          >
            <FiFileText /> {generatingPdf ? t('invoices.detail.generating') : (invoice.hasPdf ? t('invoices.detail.regeneratePdf') : t('invoices.generatePdf'))}
          </button>
          {invoice.hasPdf && (
            <>
              <button className="btn btn-secondary" onClick={handlePreviewPdf}>
                <FiEye /> {t('invoices.detail.previewPdf')}
              </button>
              <button className="btn btn-secondary" onClick={handleDownloadPdf}>
                <FiDownload /> {t('invoices.detail.downloadPdf')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-item">
          <span className="detail-label">{t('invoices.invoiceNumber')}</span>
          <span className="detail-value">{invoice.invoiceNumber}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.type')}</span>
          <span className="detail-value">
            <span className={`badge ${invoice.invoiceType === 1 ? 'badge-cancelled' : invoice.invoiceType === 2 ? 'badge-pending' : 'badge-active'}`}>
              {typeName}
            </span>
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.detail.purchaseOrder')}</span>
          <span className="detail-value">
            <Link to={`/purchase-orders/${invoice.purchaseOrderId}`}>
              {invoice.purchaseOrderNumber}
            </Link>
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.status')}</span>
          <span className="detail-value">
            <span className={`badge badge-${invoice.status === 0 ? 'draft' : invoice.status === 1 ? 'pending' : invoice.status === 2 ? 'confirmed' : invoice.status === 4 ? 'delivered' : invoice.status === 3 ? 'cancelled' : 'draft'}`}>
              {statusName}
            </span>
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.detail.amazonInvoiceId')}</span>
          <span className="detail-value">{invoice.amazonInvoiceId || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.detail.amazonTransactionId')}</span>
          <span className="detail-value">{invoice.amazonTransactionId || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.invoiceDate')}</span>
          <span className="detail-value">{formatDate(invoice.invoiceDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.dueDate')}</span>
          <span className="detail-value">{formatDate(invoice.dueDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.currency')}</span>
          <span className="detail-value">{invoice.currency}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.paymentTerms')}</span>
          <span className="detail-value">{invoice.paymentTerms || '-'}</span>
        </div>
        {invoice.notes && (
          <div className="detail-item" style={{ gridColumn: 'span 2' }}>
            <span className="detail-label">{t('invoices.notes')}</span>
            <span className="detail-value">{invoice.notes}</span>
          </div>
        )}
        {invoice.originalInvoiceId && (
          <div className="detail-item">
            <span className="detail-label">{t('invoices.detail.originalInvoice')}</span>
            <span className="detail-value">
              <Link to={`/invoices/${invoice.originalInvoiceId}`}>
                {t('invoices.detail.viewInvoice')}
              </Link>
            </span>
          </div>
        )}
        <div className="detail-item">
          <span className="detail-label">{t('invoices.detail.submittedAt')}</span>
          <span className="detail-value">{formatDate(invoice.submittedAt)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('invoices.detail.createdAt')}</span>
          <span className="detail-value">{formatDate(invoice.createdAt)}</span>
        </div>
      </div>

      {/* Items */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3>{t('invoices.invoiceItems')}</h3>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('invoices.description')}</th>
                  <th>{t('invoices.detail.productId')}</th>
                  <th>ASIN</th>
                  <th style={{ textAlign: 'right' }}>{t('invoices.quantity')}</th>
                  <th style={{ textAlign: 'right' }}>{t('invoices.unitPrice')}</th>
                  <th style={{ textAlign: 'right' }}>{t('invoices.value')}</th>
                  <th style={{ textAlign: 'right' }}>{t('invoices.detail.vatRate')}</th>
                  <th style={{ textAlign: 'right' }}>{t('invoices.detail.vatAmount')}</th>
                  <th>{t('invoices.detail.hsCode')}</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.itemSequenceNumber}</td>
                    <td>{item.description}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{item.amazonProductId}</td>
                    <td>{item.asin || '-'}</td>
                    <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.totalPrice)}</td>
                    <td style={{ textAlign: 'right' }}>{item.taxRate}%</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.taxAmount)}</td>
                    <td>{item.hsCode || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="card-body">
          <div className="totals-grid">
            <div className="total-row">
              <span className="total-label">{t('invoices.detail.netValue')}:</span>
              <span className="total-value">{formatCurrency(invoice.subTotal)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">{t('invoices.detail.vatAmount')}:</span>
              <span className="total-value">{formatCurrency(invoice.taxAmount)}</span>
            </div>
            <div className="total-row total-row-final">
              <span className="total-label">{t('invoices.detail.grossTotal')}:</span>
              <span className="total-value">{formatCurrency(invoice.totalAmount)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Credit notes */}
      {invoice.creditNotes && invoice.creditNotes.length > 0 && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <div className="card-header">
            <h3>{t('invoices.detail.creditNotes')}</h3>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('invoices.detail.number')}</th>
                    <th>{t('invoices.status')}</th>
                    <th>{t('invoices.detail.date')}</th>
                    <th style={{ textAlign: 'right' }}>{t('invoices.amount')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.creditNotes.map((cn) => (
                    <tr key={cn.id}>
                      <td>{cn.invoiceNumber}</td>
                      <td><span className="badge badge-cancelled">{cn.status}</span></td>
                      <td>{formatDate(cn.invoiceDate)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(cn.totalAmount)}</td>
                      <td>
                        <Link to={`/invoices/${cn.id}`} className="btn btn-link btn-sm">
                          <FiFileText /> {t('invoices.detail.details')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log */}
      <InvoiceAuditLogSection invoiceId={id || ''} />

      {/* PDF Preview Modal */}
      {showPdfPreview && pdfUrl && (
        <div className="modal-overlay" onClick={closePdfPreview}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', width: '900px', height: '85vh' }}
          >
            <div className="modal-header">
              <h2>{t('invoices.detail.pdfPreviewTitle', { number: invoice.invoiceNumber })}</h2>
              <button className="btn btn-icon" onClick={closePdfPreview}>
                <FiX />
              </button>
            </div>
            <div className="modal-body" style={{ padding: 0, flex: 1, overflow: 'hidden' }}>
              <iframe
                src={pdfUrl}
                style={{ width: '100%', height: '100%', border: 'none', minHeight: '60vh' }}
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}

      {/* Credit Note Modal */}
      {showCreditNoteModal && (
        <div className="modal-overlay" onClick={() => setShowCreditNoteModal(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h2>{t('invoices.detail.newCreditNote')}</h2>
              <button className="btn btn-icon" onClick={() => setShowCreditNoteModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>{t('invoices.detail.creditNoteNumber')} *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={creditNoteData.creditNoteNumber}
                    onChange={(e) =>
                      setCreditNoteData((p) => ({ ...p, creditNoteNumber: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>{t('invoices.detail.date')} *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={creditNoteData.creditNoteDate}
                    onChange={(e) =>
                      setCreditNoteData((p) => ({ ...p, creditNoteDate: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('invoices.detail.reason')}</label>
                <textarea
                  className="form-control"
                  value={creditNoteData.reason}
                  onChange={(e) =>
                    setCreditNoteData((p) => ({ ...p, reason: e.target.value }))
                  }
                  rows={2}
                  placeholder={t('invoices.detail.reasonPlaceholder')}
                />
              </div>
              <h4 style={{ marginTop: '1rem' }}>{t('invoices.detail.itemsCanChangeQty')}</h4>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('invoices.description')}</th>
                      <th>{t('invoices.quantity')}</th>
                      <th>{t('invoices.unitPrice')}</th>
                      <th>{t('invoices.value')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditNoteData.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.description}</td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={item.quantity}
                            min={0}
                            style={{ width: '80px' }}
                            onChange={(e) => {
                              const newItems = [...creditNoteData.items];
                              newItems[idx] = { ...newItems[idx], quantity: Number(e.target.value) };
                              setCreditNoteData((p) => ({ ...p, items: newItems }));
                            }}
                          />
                        </td>
                        <td>{formatCurrency(item.unitPrice)}</td>
                        <td>{formatCurrency(item.quantity * item.unitPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreditNoteModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateCreditNote}
                disabled={creatingCn || !creditNoteData.creditNoteNumber}
              >
                {creatingCn ? t('invoices.creating') : t('invoices.detail.createCreditNote')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('invoices.detail.deleteTitle')}
        message={t('invoices.detail.deleteMessage', { number: invoice?.invoiceNumber })}
        confirmLabel={t('invoices.detail.deleteInvoice')}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={deleting}
      />

      {/* Submit confirmation */}
      <ConfirmDialog
        isOpen={showSubmitConfirm}
        title={t('invoices.detail.submitTitle')}
        message={t('invoices.detail.submitMessage', { number: invoice?.invoiceNumber, items: invoice?.items.length, total: invoice ? invoice.totalAmount.toLocaleString('pl-PL', { style: 'currency', currency: invoice.currency }) : '' })}
        confirmLabel={t('invoices.submitToAmazon')}
        variant="warning"
        onConfirm={() => { setShowSubmitConfirm(false); handleSubmitToAmazon(); }}
        onCancel={() => setShowSubmitConfirm(false)}
        loading={submitting}
      />
    </div>
  );
};

// Audit Log Sub-component
const InvoiceAuditLogSection: React.FC<{ invoiceId: string }> = ({ invoiceId }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!invoiceId) return;
    const fetch = async () => {
      try {
        const res = await invoicesApi.getAuditLog(invoiceId);
        setLogs(res.data);
      } catch {
        // no audit logs
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [invoiceId]);

  if (loading) return null;
  if (logs.length === 0) return null;

  const ACTION_LABELS: Record<string, string> = {
    Created: t('invoices.audit.created'),
    StatusChanged: t('invoices.audit.statusChanged'),
    Submitted: t('invoices.audit.submitted'),
    SubmitFailed: t('invoices.audit.submitFailed'),
    Edited: t('invoices.audit.edited'),
    Deleted: t('invoices.audit.deleted'),
    PdfGenerated: t('invoices.audit.pdfGenerated'),
    CreditNoteCreated: t('invoices.audit.creditNoteCreated'),
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString('pl-PL') + ' ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  };

  const visibleLogs = expanded ? logs : logs.slice(0, 5);

  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <h3>{t('invoices.audit.title', { count: logs.length })}</h3>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {expanded ? t('invoices.audit.collapse') : t('invoices.audit.expand')}
        </span>
      </div>
      <div className="card-body" style={{ padding: '12px 16px' }}>
        <ul className="audit-timeline">
          {visibleLogs.map((log) => (
            <li key={log.id} className={`audit-item action-${log.action}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div className="audit-action">
                    {ACTION_LABELS[log.action] || log.action}
                  </div>
                  {log.details && <div className="audit-details">{log.details}</div>}
                  {(log.oldValue || log.newValue) && (
                    <div className="audit-values">
                      {log.oldValue && <span className="audit-old-value">{log.oldValue}</span>}
                      {log.oldValue && log.newValue && <span>→</span>}
                      {log.newValue && <span className="audit-new-value">{log.newValue}</span>}
                    </div>
                  )}
                  {log.userName && (
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {t('invoices.audit.by')}: {log.userName}
                    </div>
                  )}
                </div>
                <span className="audit-time">{formatTime(log.createdAt)}</span>
              </div>
            </li>
          ))}
        </ul>
        {!expanded && logs.length > 5 && (
          <button
            className="btn btn-link btn-sm"
            onClick={() => setExpanded(true)}
            style={{ marginTop: '8px' }}
          >
            {t('invoices.audit.showAll', { count: logs.length })}
          </button>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetailPage;
